/**
 * NTN Verify Hub™ — public marketing landing page (Enterprise Demo Rewrite).
 *
 * This page opens live customer demonstrations for enterprise property
 * management buyers. The presenter walks through the page before signing
 * into the platform, so:
 *
 *  - The story must be readable in ~30 seconds on a projector
 *  - The tone must feel like a mature national SaaS company
 *  - Real product screenshots (`/landing/preview_*.jpg`) do the persuading,
 *    not abstract graphics
 *  - Every section must earn its place; nothing decorative
 *
 * Section order (fixed by the user brief):
 *   1. Sticky nav
 *   2. Hero (headline · subhead · dual CTAs · real dashboard screenshot)
 *   3. Problem (fragmented raw-data screening → verified intelligence)
 *   4. How It Works (5-step visual workflow)
 *   5. Platform Preview (4 real product screenshots)
 *   6. Verification Services (6 services as one platform)
 *   7. Why Property Managers Choose Verify Hub (8 outcome cards)
 *   8. Why NTN (inverted navy band — human + technology differentiator)
 *   9. Closing CTA (Schedule a Demo)
 *
 * Type / palette matches the product surfaces (Playfair Display serif for
 * display, Manrope for body, navy `#0A192F` primary) so the transition into
 * the live demo feels seamless.
 */
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, ArrowUpRight, ShieldCheck, ShieldAlert, FileWarning,
  Search, FileUp, Cpu, Target, CheckCircle2, FileText, Briefcase,
  Home as HomeIcon, FileBadge, Scale, Fingerprint, TrendingDown,
  Clock, UserCheck, Zap, Layers, LayoutDashboard, Timer, PhoneOff,
  Menu as MenuIcon,
} from "lucide-react";

/* --------------------------------------------------------------------------
 * Small primitives — kept local so the file stays a single readable page
 * ------------------------------------------------------------------------ */

const Overline = ({ children, tone = "slate", testid }) => {
  const toneClass = {
    slate: "text-[#1F3B8C]",
    navy: "text-[#0A192F]",
    invert: "text-[#B8C4E5]",
    accent: "text-emerald-700",
  }[tone];
  return (
    <div
      className={`text-[11px] font-bold tracking-[0.26em] uppercase ${toneClass}`}
      data-testid={testid}
    >
      {children}
    </div>
  );
};

const SectionHead = ({ overline, title, sub, align = "left", tone = "light" }) => (
  <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
    {overline && (
      <Overline tone={tone === "dark" ? "invert" : "slate"} testid={`section-overline`}>
        {overline}
      </Overline>
    )}
    <h2
      className={`font-display font-semibold leading-[1.1] mt-4 ${
        tone === "dark" ? "text-white" : "text-[#0A192F]"
      } text-4xl md:text-5xl`}
    >
      {title}
    </h2>
    {sub && (
      <p
        className={`mt-5 text-[17px] md:text-[19px] leading-[1.55] ${
          tone === "dark" ? "text-white/75" : "text-slate-700"
        }`}
      >
        {sub}
      </p>
    )}
  </div>
);

/** Real screenshot in a subtle "device chrome" frame. Used across the page. */
const BrowserFrame = ({ src, alt, className = "", testid, accent = "blue" }) => (
  <figure
    className={`relative rounded-xl overflow-hidden border border-slate-200 bg-white ring-1 ring-[#1F3B8C]/5 shadow-[0_40px_80px_-24px_rgba(31,59,140,0.28),0_18px_36px_-18px_rgba(10,25,47,0.18)] ${className}`}
    data-testid={testid}
  >
    <div className="h-9 border-b border-slate-100 flex items-center px-4 gap-1.5 bg-gradient-to-b from-slate-50 to-white">
      <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]/60" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]/60" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]/60" />
      <span className="ml-4 text-[10px] tracking-[0.16em] uppercase text-slate-400 font-mono truncate">
        ntnverify.com
      </span>
    </div>
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="block w-full h-auto"
    />
  </figure>
);

/* --------------------------------------------------------------------------
 * Data
 * ------------------------------------------------------------------------ */

const HOW_STEPS = [
  { icon: FileUp,       title: "Submit Applicant Package",       desc: "Property manager uploads the completed application and supporting documents." },
  { icon: ShieldCheck,  title: "NTN Performs Verification",      desc: "Our specialists verify employment, residency, income, and identity." },
  { icon: ShieldAlert,  title: "Risk Intelligence Generated",    desc: "Fraud indicators, discrepancies, and observations surface as intelligence." },
  { icon: Cpu,          title: "DecisionPoint Completed",        desc: "A recommendation is compiled with scoring, findings, and supporting evidence." },
  { icon: Target,       title: "Decision-Ready Applicant",       desc: "You receive a verified applicant ready for a confident leasing decision." },
];

const PREVIEWS = [
  {
    src: "/landing/preview_dashboard.jpg",
    tag: "Dashboard",
    title: "Every applicant, every stage — one view.",
    caption: "Live pipeline, priorities, and risk intelligence in a single glance.",
  },
  {
    src: "/landing/preview_screening.jpg",
    tag: "Decision-Ready Applicant",
    title: "The Executive Summary tells the story in seconds.",
    caption: "Workflow status, recommendation, risk indicators, and progress — above the fold.",
  },
  {
    src: "/landing/preview_risk_intel.jpg?v=2",
    tag: "Risk Intelligence",
    title: "Risk observations, explained in plain language.",
    caption: "Manual NTN classification with every indicator surfaced by severity and context — not a raw report your team has to interpret.",
  },
  {
    src: "/landing/preview_decisionpoint.jpg",
    tag: "DecisionPoint Summary",
    title: "One score. One recommendation.",
    caption: "Supporting intelligence — DecisionPoint, FICO, eviction, and criminal signals — attached to every applicant.",
  },
];

const SERVICES = [
  { icon: FileBadge,   name: "DecisionPoint Report",   desc: "Composite score and recommendation with supporting intelligence." },
  { icon: Briefcase,   name: "Employment Verification", desc: "Human-verified employer, income, and tenure." },
  { icon: HomeIcon,    name: "Landlord Verification",   desc: "Rental history and payment behavior confirmed with prior landlords." },
  { icon: FileText,    name: "Document Validation",     desc: "Authenticity review of identity, pay stubs, tax records, and bank statements." },
  { icon: Scale,       name: "Criminal Report",         desc: "Multi-jurisdiction criminal history search." },
  { icon: Fingerprint, name: "SSN Verification",        desc: "Identity confirmation and synthetic-identity screening." },
];

const OUTCOMES = [
  { icon: TrendingDown,    title: "Reduce Application Fraud",       desc: "Human verifiers catch synthetic identities and forged documents." },
  { icon: Clock,           title: "Save Staff Time",                desc: "Stop chasing employers, landlords, and paperwork." },
  { icon: UserCheck,       title: "Human Verification",             desc: "Every applicant reviewed by an NTN specialist." },
  { icon: CheckCircle2,    title: "Decision-Ready Recommendations", desc: "A clear approve · conditional · decline signal on every file." },
  { icon: Layers,          title: "Centralized Intelligence",       desc: "Employment, residency, and document review in one record." },
  { icon: LayoutDashboard, title: "One Dashboard",                  desc: "Every applicant, every property, every stage — one operational surface." },
  { icon: Timer,           title: "Faster Leasing Decisions",       desc: "Cut days out of the approval cycle without cutting corners." },
  { icon: PhoneOff,        title: "Fewer Manual Verification Calls", desc: "NTN handles employer and landlord outreach on your behalf." },
];

/* --------------------------------------------------------------------------
 * Page
 * ------------------------------------------------------------------------ */

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased selection:bg-[#0A192F] selection:text-white">

      {/* ==================================================================
       * 1. STICKY NAV — glass on white, section anchors, dual right CTAs
       * ================================================================ */}
      <header
        className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200"
        data-testid="nav-header"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="nav-home">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-8 text-[13px] font-semibold text-slate-600 tracking-[0.01em]">
            <a href="#problem"   className="hover:text-[#1F3B8C] transition-colors">Problem</a>
            <a href="#how"       className="hover:text-[#1F3B8C] transition-colors">How It Works</a>
            <a href="#platform"  className="hover:text-[#1F3B8C] transition-colors">Platform</a>
            <a href="#services"  className="hover:text-[#1F3B8C] transition-colors">Services</a>
            <a href="#why-ntn"   className="hover:text-[#1F3B8C] transition-colors">Why NTN</a>
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="hidden sm:inline-flex items-center h-9 px-4 text-[13px] font-semibold text-slate-700 hover:text-[#1F3B8C] transition-colors"
              data-testid="nav-signin"
            >
              Sign in
            </Link>
            <Button
              asChild
              className="h-10 px-5 text-[13px] font-semibold rounded-md bg-[#1F3B8C] text-white hover:bg-[#182F72] shadow-[0_8px_20px_-8px_rgba(31,59,140,0.55)] tracking-[0.01em]"
              data-testid="nav-cta-demo"
            >
              <a href="#schedule">Schedule a Demo</a>
            </Button>
          </div>
        </div>
      </header>

      {/* ==================================================================
       * 2. HERO — asymmetric left content · right screenshot floating
       * ================================================================ */}
      <section className="relative overflow-hidden bg-white" data-testid="hero-section">
        {/* Branded gradient wash — NTN royal blue radial + subtle grid */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute -top-40 -right-40 w-[720px] h-[720px] rounded-full opacity-[0.10]"
            style={{ background: "radial-gradient(closest-side, #1F3B8C, transparent 70%)" }}
          />
          <div
            className="absolute -bottom-32 -left-40 w-[520px] h-[520px] rounded-full opacity-[0.05]"
            style={{ background: "radial-gradient(closest-side, #0A192F, transparent 70%)" }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-14 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-y-12 lg:gap-x-10 items-center">
            {/* Left — narrative */}
            <div className="lg:col-span-6" data-testid="hero-content">
              <div className="inline-flex items-center gap-2 h-7 px-3 rounded-full bg-[#1F3B8C]/8 border border-[#1F3B8C]/15" data-testid="hero-badge">
                <span className="h-1.5 w-1.5 rounded-full bg-[#1F3B8C]" />
                <span className="text-[10.5px] font-bold tracking-[0.22em] uppercase text-[#1F3B8C]">
                  Applicant Verification &amp; Risk Intelligence
                </span>
              </div>
              <h1 className="font-display-xl font-medium leading-[1.02] text-[52px] md:text-[64px] lg:text-[76px] mt-6 text-[#0A192F]">
                Verified applicants.
                <br />
                <span className="text-[#1F3B8C]">Confident decisions.</span>
              </h1>
              <p className="mt-7 max-w-xl text-[18px] md:text-[19px] leading-[1.55] text-slate-700" data-testid="hero-subhead">
                Property managers submit an applicant package. NTN performs the verification.
                You receive a decision-ready applicant with published reports, risk intelligence,
                and a clear recommendation.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="hero-ctas">
                <Button
                  asChild
                  className="h-12 px-7 rounded-md bg-[#1F3B8C] text-white hover:bg-[#182F72] text-[15px] font-semibold shadow-[0_10px_30px_-10px_rgba(31,59,140,0.55)] tracking-[0.01em]"
                  data-testid="hero-cta-primary"
                >
                  <a href="#schedule" className="inline-flex items-center gap-2">
                    Schedule a Demo <ArrowRight className="h-4 w-4" strokeWidth={2} />
                  </a>
                </Button>
                <Button
                  asChild
                  variant="ghost"
                  className="h-12 px-6 rounded-md text-[15px] font-semibold text-[#0A192F] hover:text-[#1F3B8C] hover:bg-slate-50 border border-slate-200 tracking-[0.01em]"
                  data-testid="hero-cta-secondary"
                >
                  <Link to="/login" className="inline-flex items-center gap-2">
                    View Platform <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
                  </Link>
                </Button>
              </div>

              {/* Trust strip — no fake logos, capability language only */}
              <div className="mt-14 pt-6 border-t border-slate-200 max-w-lg" data-testid="hero-trust-strip">
                <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-500">
                  Trusted by national property management leaders for decision-ready intelligence
                </p>
              </div>
            </div>

            {/* Right — floating dashboard screenshot */}
            <div className="lg:col-span-6 relative lg:pl-6" data-testid="hero-visual">
              <BrowserFrame
                src="/landing/preview_dashboard.jpg"
                alt="NTN Verify Hub dashboard showing the property manager pipeline, risk intelligence, and applicant list."
                className="transform lg:-rotate-[0.75deg] lg:hover:rotate-0 transition-transform duration-700 ease-out"
                testid="hero-visual-dashboard"
              />
              {/* Accent — a floating "verification report" card overlapping the dashboard */}
              <div
                className="hidden lg:block absolute -bottom-10 -left-6 w-[240px] rounded-lg bg-white border border-slate-200 shadow-[0_24px_50px_-16px_rgba(10,25,47,0.24)] p-5"
                data-testid="hero-visual-callout"
              >
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-emerald-500 ring-4 ring-emerald-500/15" />
                  <span className="text-[10px] tracking-[0.22em] uppercase text-emerald-700 font-bold">Verified</span>
                </div>
                <div className="mt-3 font-display font-semibold text-lg text-[#0A192F] leading-tight">Employment Confirmed</div>
                <div className="mt-1.5 text-[12px] text-slate-600 leading-snug">Employer, income, and tenure human-verified by NTN.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================================================================
       * 3. PROBLEM — split pane, "fragmented" vs "verified intelligence"
       * ================================================================ */}
      <section id="problem" className="relative bg-[#F5F7FB] border-y border-slate-200 py-24 lg:py-28" data-testid="problem-section">
        {/* Brand accent top rule */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#1F3B8C]/25 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionHead
            overline="The Problem"
            title="Traditional applicant screening is fragmented."
            sub="Property managers get piles of raw reports, then spend hours calling employers, chasing landlords, reviewing pay stubs, hunting for fraud, and making difficult approval calls without a clear signal."
          />

          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {/* Left — the current-state mess */}
            <div className="bg-white p-10 md:p-12 rounded-xl border border-slate-200 shadow-[0_10px_40px_-20px_rgba(10,25,47,0.15)]" data-testid="problem-before">
              <Overline tone="slate">Today · Raw Data</Overline>
              <h3 className="font-display font-semibold text-2xl md:text-[30px] mt-4 leading-[1.15] text-slate-500">
                Reports arrive. The work begins.
              </h3>
              <ul className="mt-7 space-y-3.5">
                {[
                  { icon: FileWarning, label: "Reviewing raw screening reports" },
                  { icon: Search,      label: "Calling employers and landlords" },
                  { icon: FileText,    label: "Reading pay stubs and bank statements" },
                  { icon: ShieldAlert, label: "Hunting for fraud on your own" },
                  { icon: Scale,       label: "Making judgment calls without a clear signal" },
                ].map(({ icon: Icon, label }) => (
                  <li key={label} className="flex items-start gap-3 text-slate-500">
                    <span className="mt-0.5 h-6 w-6 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                      <Icon className="h-3.5 w-3.5 text-slate-400" strokeWidth={2} />
                    </span>
                    <span className="text-[15px] font-medium">{label}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right — the Verify Hub state */}
            <div className="relative bg-white p-10 md:p-12 rounded-xl border-2 border-[#1F3B8C]/25 shadow-[0_20px_60px_-24px_rgba(31,59,140,0.35)]" data-testid="problem-after">
              <div className="absolute top-0 left-0 right-0 h-1 rounded-t-xl bg-gradient-to-r from-[#1F3B8C] via-emerald-500 to-[#1F3B8C]" />
              <Overline tone="accent">Verify Hub · Verified Intelligence</Overline>
              <h3 className="font-display font-semibold text-2xl md:text-[30px] mt-4 leading-[1.15] text-[#0A192F]">
                A decision-ready applicant is delivered.
              </h3>
              <ul className="mt-7 space-y-3.5">
                {[
                  "Published verification reports for employment, landlord, and documents",
                  "Risk indicators explained in plain language",
                  "A single DecisionPoint recommendation with full audit trail",
                  "Every finding reviewed and confirmed by an NTN specialist",
                  "One dashboard across every property and every applicant",
                ].map((label) => (
                  <li key={label} className="flex items-start gap-3 text-slate-800">
                    <span className="mt-0.5 h-6 w-6 rounded-md bg-emerald-50 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2.25} />
                    </span>
                    <span className="text-[15px] leading-relaxed font-medium">{label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ==================================================================
       * 4. HOW IT WORKS — 5 steps, horizontal on desktop, vertical mobile
       * ================================================================ */}
      <section id="how" className="py-24 lg:py-28 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionHead
            overline="How It Works"
            title="A five-step managed workflow."
            sub="Every applicant follows the same predictable path — from package submission to a decision-ready recommendation."
          />

          <ol className="mt-16 relative">
            {/* Desktop horizontal connector — branded gradient rule */}
            <div className="hidden lg:block absolute left-[6%] right-[6%] top-8 h-[2px] bg-gradient-to-r from-[#1F3B8C]/10 via-[#1F3B8C]/40 to-[#1F3B8C]/10" aria-hidden />
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-6">
              {HOW_STEPS.map(({ icon: Icon, title, desc }, i) => (
                <li key={title} className="relative flex lg:flex-col gap-4 lg:gap-0" data-testid={`how-step-${i + 1}`}>
                  {/* Step marker */}
                  <div className="relative shrink-0 lg:mb-6">
                    <div className="h-16 w-16 rounded-full bg-gradient-to-b from-white to-[#F5F7FB] border border-[#1F3B8C]/15 shadow-[0_10px_30px_-10px_rgba(31,59,140,0.28)] flex items-center justify-center">
                      <Icon className="h-6 w-6 text-[#1F3B8C]" strokeWidth={1.75} />
                    </div>
                    <div className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-[#1F3B8C] text-white text-[11px] font-mono font-bold flex items-center justify-center ring-4 ring-white">
                      {i + 1}
                    </div>
                  </div>
                  <div className="pt-1 lg:pt-0">
                    <div className="font-display font-semibold text-lg md:text-xl text-[#0A192F] leading-snug">{title}</div>
                    <div className="mt-2 text-[14px] text-slate-600 leading-relaxed max-w-[220px]">{desc}</div>
                  </div>
                </li>
              ))}
            </div>
          </ol>
        </div>
      </section>

      {/* ==================================================================
       * 5. PLATFORM PREVIEW — sticky left, scrolling right column of shots
       * ================================================================ */}
      <section id="platform" className="relative py-24 lg:py-28 bg-gradient-to-b from-[#0A192F] via-[#0D2044] to-[#0A192F] border-y border-[#1F3B8C]/20 overflow-hidden" data-testid="platform-preview-section">
        {/* Subtle brand grid */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: "linear-gradient(#1F3B8C 1px, transparent 1px), linear-gradient(90deg, #1F3B8C 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        {/* Brand accent glow */}
        <div className="absolute top-1/4 -left-40 w-[520px] h-[520px] rounded-full opacity-20 blur-3xl pointer-events-none"
             style={{ background: "radial-gradient(closest-side, #1F3B8C, transparent 70%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-14">
            {/* Sticky left column */}
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-24">
                <Overline tone="invert">Platform</Overline>
                <h2 className="font-display font-semibold leading-[1.1] mt-4 text-4xl md:text-5xl text-white">
                  What your team actually uses.
                </h2>
                <p className="mt-5 text-[17px] text-white/70 leading-[1.55]">
                  Every screen designed around one question: what do I need to make a confident leasing decision?
                </p>
                <Button
                  asChild
                  className="mt-7 h-10 px-5 rounded-md text-[13px] font-semibold bg-white text-[#0A192F] hover:bg-white/90 shadow-[0_8px_20px_-8px_rgba(0,0,0,0.4)] tracking-[0.01em]"
                  data-testid="platform-cta-view"
                >
                  <Link to="/login" className="inline-flex items-center gap-2">
                    View the platform <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right column — the four screenshots */}
            <div className="lg:col-span-8 space-y-20 lg:space-y-24">
              {PREVIEWS.map((p, i) => (
                <article key={p.tag} data-testid={`platform-preview-${i + 1}`}>
                  <div className="flex items-baseline gap-3 mb-4">
                    <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/40 font-bold">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <Overline tone="invert">{p.tag}</Overline>
                  </div>
                  <h3 className="font-display font-semibold text-2xl md:text-[30px] leading-[1.15] text-white max-w-xl">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-[15px] text-white/60 leading-relaxed italic font-serif max-w-lg">
                    {p.caption}
                  </p>
                  <BrowserFrame
                    src={p.src}
                    alt={`${p.tag} — ${p.caption}`}
                    className="mt-7"
                    testid={`platform-shot-${i + 1}`}
                  />
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==================================================================
       * 6. VERIFICATION SERVICES — technical grid, one platform
       * ================================================================ */}
      <section id="services" className="py-24 lg:py-28 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionHead
            overline="Verification Services"
            title="One managed platform. Six verification services."
            sub="NTN performs each service on behalf of your property management team. Every finding is reviewed, published, and delivered as part of a single applicant record."
          />

          <div className="mt-14 border border-slate-200 rounded-xl overflow-hidden bg-white shadow-[0_10px_40px_-20px_rgba(31,59,140,0.15)]">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {SERVICES.map(({ icon: Icon, name, desc }, i) => (
                <div
                  key={name}
                  className={`group p-8 md:p-10 hover:bg-[#F5F7FB] transition-colors relative
                    ${i % 3 !== 2 ? "lg:border-r" : ""}
                    ${i % 2 !== 1 ? "sm:border-r lg:border-r" : ""}
                    ${i < SERVICES.length - (SERVICES.length % 3 || 3) ? "border-b" : "sm:border-b lg:border-b-0"}
                    border-slate-200`}
                  data-testid={`service-${name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="h-12 w-12 rounded-lg bg-[#1F3B8C]/8 border border-[#1F3B8C]/15 flex items-center justify-center group-hover:bg-[#1F3B8C] group-hover:border-[#1F3B8C] transition-colors">
                    <Icon className="h-5 w-5 text-[#1F3B8C] group-hover:text-white transition-colors" strokeWidth={1.75} />
                  </div>
                  <div className="font-display font-semibold text-xl md:text-[22px] text-[#0A192F] mt-5 leading-snug">{name}</div>
                  <p className="mt-2 text-[14px] text-slate-600 leading-relaxed max-w-xs">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==================================================================
       * 7. WHY PROPERTY MANAGERS CHOOSE VERIFY HUB — outcome cards
       * ================================================================ */}
      <section id="outcomes" className="relative py-24 lg:py-28 bg-[#F5F7FB] border-y border-slate-200" data-testid="why-verify-hub-section">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#1F3B8C]/25 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionHead
            overline="Why Verify Hub"
            title="Outcomes property managers can measure."
            sub="This is a fully managed applicant verification platform — not a raw-data screening product. Everything below is a direct outcome your team receives."
          />

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {OUTCOMES.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="group bg-white p-7 rounded-xl border border-slate-200 hover:border-[#1F3B8C]/40 hover:shadow-[0_16px_40px_-20px_rgba(31,59,140,0.30)] hover:-translate-y-0.5 transition-all duration-300"
                data-testid={`outcome-${title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="h-10 w-10 rounded-lg bg-[#1F3B8C]/8 border border-[#1F3B8C]/15 flex items-center justify-center group-hover:bg-[#1F3B8C] transition-colors">
                  <Icon className="h-5 w-5 text-[#1F3B8C] group-hover:text-white transition-colors" strokeWidth={1.75} />
                </div>
                <div className="font-display font-semibold text-lg text-[#0A192F] mt-5 leading-snug">{title}</div>
                <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================================================================
       * 8. WHY NTN — inverted navy band, editorial
       * ================================================================ */}
      <section id="why-ntn" className="relative bg-[#0A192F] text-white overflow-hidden" data-testid="why-ntn-section">
        {/* subtle noise texture for editorial depth */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>\")",
            mixBlendMode: "overlay",
          }}
        />
        {/* Royal blue glow */}
        <div className="absolute top-0 right-0 w-[620px] h-[620px] rounded-full opacity-25 blur-3xl pointer-events-none"
             style={{ background: "radial-gradient(closest-side, #1F3B8C, transparent 70%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-14 items-start">
            <div className="lg:col-span-7">
              <Overline tone="invert">Why NTN</Overline>
              <h2 className="font-display-xl font-medium leading-[1.05] text-4xl md:text-5xl lg:text-[56px] mt-5 text-white">
                Human verification specialists,
                <br />
                <span className="text-[#B8C4E5]">supported by technology.</span>
              </h2>
              <p className="mt-7 text-[17px] md:text-[19px] leading-[1.55] text-white/75 max-w-xl">
                Fully automated screening services return raw data and leave the interpretation
                to you. Verify Hub is different. Every applicant is reviewed by an experienced
                NTN verification specialist — the technology exists to support that specialist,
                not replace them.
              </p>

              <div className="mt-10 grid sm:grid-cols-3 gap-6 max-w-2xl">
                {[
                  { label: "Reviewed by",           value: "Human specialists" },
                  { label: "Backed by",             value: "Verification technology" },
                  { label: "Delivered as",          value: "Published reports" },
                ].map(({ label, value }) => (
                  <div key={label} className="border-l-2 border-[#1F3B8C]/50 pl-4">
                    <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#B8C4E5]/70">{label}</div>
                    <div className="mt-2 font-display font-semibold text-lg text-white leading-snug">{value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — statement card */}
            <div className="lg:col-span-5" data-testid="why-ntn-pullquote">
              <div className="relative rounded-xl border border-[#1F3B8C]/25 bg-[#1F3B8C]/[0.08] p-8 md:p-10 backdrop-blur-sm shadow-[0_30px_60px_-20px_rgba(0,0,0,0.5)]">
                <div
                  aria-hidden
                  className="absolute -top-6 left-6 font-serif text-[96px] leading-none text-[#B8C4E5]/25 select-none"
                >
                  “
                </div>
                <p className="font-display font-medium text-2xl md:text-[28px] leading-[1.25] text-white pt-6">
                  Verified intelligence — not raw data — is what enterprise property management deserves.
                </p>
                <div className="mt-6 pt-6 border-t border-[#1F3B8C]/25">
                  <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#B8C4E5]">NTN Verify Hub</div>
                  <div className="mt-1 text-[13px] text-white/70">Applicant verification &amp; risk intelligence</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================================================================
       * 9. CLOSING CTA — Schedule a demo
       * ================================================================ */}
      <section id="schedule" className="relative py-24 lg:py-32 bg-white overflow-hidden" data-testid="closing-cta-section">
        {/* Subtle brand glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[720px] h-[520px] opacity-[0.05]"
               style={{ background: "radial-gradient(ellipse, #1F3B8C, transparent 70%)" }} />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 md:px-10 lg:px-14 text-center">
          <Overline testid="closing-overline">Ready when you are</Overline>
          <h2 className="font-display-xl font-medium leading-[1.05] text-4xl md:text-5xl lg:text-[56px] mt-5 text-[#0A192F]">
            See NTN Verify Hub in action.
          </h2>
          <p className="mt-6 text-[17px] md:text-[19px] text-slate-700 leading-[1.55] max-w-xl mx-auto">
            A 30-minute walkthrough with our team — dashboard, screening detail, published reports,
            and DecisionPoint — using real applicant scenarios.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3" data-testid="closing-ctas">
            <Button
              asChild
              className="h-12 px-7 rounded-md bg-[#1F3B8C] text-white hover:bg-[#182F72] text-[15px] font-semibold shadow-[0_12px_30px_-10px_rgba(31,59,140,0.55)] tracking-[0.01em]"
              data-testid="closing-cta-primary"
            >
              <Link to="/schedule-demo" className="inline-flex items-center gap-2">
                Schedule a Demo <ArrowRight className="h-4 w-4" strokeWidth={2} />
              </Link>
            </Button>
            <Button
              asChild
              variant="ghost"
              className="h-12 px-6 rounded-md text-[15px] font-semibold text-[#0A192F] hover:text-[#1F3B8C] hover:bg-slate-50 border border-slate-200 tracking-[0.01em]"
              data-testid="closing-cta-secondary"
            >
              <Link to="/login" className="inline-flex items-center gap-2">
                View Platform <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ==================================================================
       * FOOTER
       * ================================================================ */}
      <footer className="bg-[#0A192F] text-white/70" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-12 grid md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center">
              <Logo light />
            </div>
            <p className="mt-4 text-[13px] leading-relaxed text-white/60 max-w-sm">
              Applicant Verification &amp; Risk Intelligence for enterprise property management.
              Verified applicants. Confident decisions.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 text-[13px]">
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">Platform</div>
              <div className="mt-3 space-y-2">
                <a href="#platform" className="block hover:text-white transition-colors">Product tour</a>
                <a href="#services" className="block hover:text-white transition-colors">Services</a>
                <a href="#how" className="block hover:text-white transition-colors">How it works</a>
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">Company</div>
              <div className="mt-3 space-y-2">
                <a href="#why-ntn" className="block hover:text-white transition-colors">Why NTN</a>
                <a href="#schedule" className="block hover:text-white transition-colors">Schedule a demo</a>
                <Link to="/login" className="block hover:text-white transition-colors">Sign in</Link>
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">Resources</div>
              <div className="mt-3 space-y-2">
                <a
                  href="https://ntnonline.com/consumers/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:text-white transition-colors"
                  data-testid="footer-link-consumers"
                >
                  Consumers
                </a>
                <a
                  href="https://ntnonline.com/property-managers/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:text-white transition-colors"
                  data-testid="footer-link-property-managers"
                >
                  Property Managers
                </a>
              </div>
            </div>
          </div>
          <div className="md:text-right">
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">Contact</div>
            <a href="mailto:demo@ntnverify.com" className="mt-3 inline-block hover:text-white transition-colors text-[13px]">
              demo@ntnverify.com
            </a>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-5 text-[11px] text-white/50 flex flex-col sm:flex-row items-center gap-2 justify-between">
            <div>© {new Date().getFullYear()} NTN Verify Hub. All rights reserved.</div>
            <div>Applicant Verification &amp; Risk Intelligence</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
